import { SelectOption } from '@globaleris/ui.library'
import moment from 'moment'
import { PriceModelQry } from '../../api/Argentia/endpoints/pricing/model/prices'
import { PriceValueModelQry } from '../../api/Argentia/endpoints/pricing/model/values'
import {
  QuoteContractModel,
  QuotesModel,
  QuotesModelQry
} from '../../api/Argentia/endpoints/quotes/model/quotes'
import i18n from '../../i18n'
import { PriceModelQryDataTable, QuotesProductTableModel } from './quotes.model'

export const mapQueryLeadToPricesListDataTable = (
  prices: PriceModelQry[]
): PriceModelQryDataTable[] => {
  return prices.map(price => mapQueryLeadToPriceListDataTable(price))
}

export const mapQueryLeadToPriceListDataTable = (price: PriceModelQry): PriceModelQryDataTable => {
  return {
    id: price.id,
    priceComponentId: price.priceComponentId,
    code: price.code,
    description: price.description,
    expression: price.expression,
    priceType: price.priceType,
    priceValueArray:
      price.priceValues.length > 0 ? mapPriceValueToPriceListArrayDataTable(price.priceValues) : [],
    currentValuesString: price.currentValuesString
      ? price.currentValuesString
      : [i18n.t('quotes:components.priceListTable.dataTable.columns.noTodayValue')],
    lgManual: price.lgManual
  }
}

export const mapPriceValueToPriceListArrayDataTable = (
  priceValues: PriceValueModelQry[]
): number[] => {
  // value can be undefined so flatMap suits better because returns an array where [] items are filtered
  return priceValues.flatMap(priceValue =>
    priceValue.value !== undefined ? [priceValue.value] : []
  )
}

export const mapQueryModelToFormData = (quoteFormData: QuotesModelQry): QuotesModel => {
  return {
    id: quoteFormData.id,
    code: quoteFormData.code,
    description: quoteFormData.description,
    startDate: quoteFormData.startDate,
    endDate: quoteFormData.endDate,
    state: quoteFormData.state,
    publishDate: quoteFormData.publishDate,
    validityDate: quoteFormData.validityDate
  }
}

export const mapServiceResponseToQuotesProductCard = (
  quotes: QuotesModel[],
  productId: number
): { specificProductQuotes: QuotesProductTableModel[]; otherQuotes: QuotesProductTableModel[] } => {
  let specificProductQuotes: QuotesProductTableModel[] = []
  let otherQuotes: QuotesProductTableModel[] = []

  quotes.forEach(quote => {
    const mappedQuote = mapQuoteModelToQuotesProductCard(quote)

    if (productId && quote.lead?.product?.id === productId) {
      specificProductQuotes.push(mappedQuote)
    } else {
      otherQuotes.push(mappedQuote)
    }
  })

  return { specificProductQuotes, otherQuotes }
}

export const mapQuoteModelToQuotesProductCard = (quote: QuotesModel): QuotesProductTableModel => {
  let signDate = ''
  let startDate = ''
  let endDate = ''
  let contractId = 0

  if (quote.quoteContracts && quote.quoteContracts.length > 0) {
    signDate = quote.quoteContracts[0].signDate ?? ''
    startDate = quote.quoteContracts[0].startDate ?? ''
    endDate = quote.quoteContracts[0].endDate ?? ''
    contractId = quote.quoteContracts[0].id ?? 0
  }

  return {
    quoteId: quote.id,
    contractId,
    code: quote.code,
    description: quote.description,
    signDate,
    startDate,
    endDate
  }
}

export const mapQuoteContractFormDataToRequest = (
  formData: QuotesProductTableModel,
  contractId: number
): QuoteContractModel => {
  return {
    id: formData.quoteId,
    contract: {
      id: contractId,
      code: formData.code ?? '',
      description: formData.description ?? ''
    },
    signDate: moment(formData.signDate).format('YYYY-MM-DD'),
    startDate: moment(formData.startDate).format('YYYY-MM-DD'),
    endDate: formData.endDate ? moment(formData.endDate).format('YYYY-MM-DD') : undefined
  }
}

export const mapQuoteModelListToSelectOption = (
  quotes: QuotesModel[],
  productQuotes: QuotesProductTableModel[]
): SelectOption[] => {
  return quotes
    .map(quote =>
      quote && !productQuotes.some((q: QuotesProductTableModel) => q.quoteId === quote.id)
        ? mapQuoteModelItemToSelectOption(quote, productQuotes)
        : null
    )
    .filter((option: SelectOption | null) => option !== null)
}

const mapQuoteModelItemToSelectOption = (
  quote: QuotesModel,
  productQuotes: QuotesProductTableModel[]
): SelectOption => {
  return {
    value: quote.id,
    label: `${quote?.lead?.product?.code} - ${quote.description}`
  }
}
